import { take, BehaviorSubject } from 'rxjs';
import eventAttributes from './EventAttributes.mjs';
import eventTypeEnum from './EventTypeEnum.mjs';

/**
 * @module EventService.EventFilter
 */
function createEventFilter(
    userService,
    deviceService,
    logService,
    util,
    _window,
    environment
) {
    const _eventFilter = {};
    let filterInfoSubject = new BehaviorSubject();
    let filters;
    let uiSettingsSubscription;

    const _person = eventAttributes.person.id;
    const _object = eventAttributes.object.id;
    const _pet = eventAttributes.pet.id;
    const _car = eventAttributes.car.id;
    const _favorite = eventAttributes.favorite.id;
    const _delivery = eventAttributes.delivery.id;
    const _none = eventAttributes.none.id;
    const _garageOpen = eventAttributes.garageOpen.id;
    const _garageClose = eventAttributes.garageClose.id;
    const _highlight = eventAttributes.highlight.id;
    const _pin = eventAttributes.pin.id;
    const _call = eventAttributes.call.id;
    const _autoSecure = eventAttributes.autoSecure.id;
    const _animal = eventAttributes.animal.id;
    const _vehicle = eventAttributes.vehicle.id;
    const _package = eventAttributes.package.id;

    const _deviceId = 'deviceId';
    const _devices = 'devices';
    const _ids = 'ids';
    const _smart = 'smart';
    const _props = 'props';
    const _other = 'other';
    const _user = 'user';
    const _ownerAlias = 'ownerAlias';
    const _highlightType = 'HIGHLIGHT_VIDEO';

    // function log(params) {
    //     params.module = 'eventFilter';
    //     logService.log(params);
    //     return params;
    // }

    function partnerAppKey() {
        if (environment.partnerId === 'myQ') {
            return 'myqMobile';
        }
        if (environment.partnerId === 'myQBusiness') {
            return 'myqb';
        }
        if (environment.partnerId === 'TendSecure') {
            return 'mobileDvrTend';
        }
        return 'mobileDvr';
    }

    function filterLifeSeconds() {
        if (environment.partnerId === 'myQ') {
            return 20 * 60;
        }
    }

    function byFilterContext(o) {
        return o.spec
            && o.spec.deviceId === this.deviceId
            && util.aliasWithNoGuidPrefix(o.spec.ownerAlias) === util.aliasWithNoGuidPrefix(this.ownerAlias);
    }

    // items filtered out by internalFilter do not affect event counts
    function internalFilter(event, filterState) {
        // do not allow fragmented events without any clips
        if ((event.type === eventTypeEnum.fmp4 || event.type === eventTypeEnum.video)
                && (!event?.media?.videoCdn?.num)) {
            return false;
        }

        // filter out events not matching ownerAlias
        if (!event.device || (filterState?.spec.ownerAlias
                && (util.aliasWithNoGuidPrefix(filterState?.spec.ownerAlias)
                    !== util.aliasWithNoGuidPrefix(event.device.ownership.ownerAlias)))) {
            return false;
        }

        // filter out end-of-life events
        if (event?.device?.eolInfo?.status === 'ended' && event?.device?.eolInfo?.startDate < event?.date) {
            return false;
        }

        // filter out certain event types
        if (event.type === eventTypeEnum.camLowBattery
                || event.type === eventTypeEnum.camShutdown
                || event.type === eventTypeEnum.camOnline
                || event.type === eventTypeEnum.callButton
                || event.type === eventTypeEnum.pinUsed
                || event.type === eventTypeEnum.invalidPin) {
            return false;
        }

        return true;
    }

    function devicesFilter(event, filterState) {
        return !filterState.devices
            || (filterState.devices.indexOf(event.srcId) > -1)
            || (event.srcType === _user);
    }

    function identityFilter(event, filterState) {
        if (!filterState.ids) {
            return true;
        }

        // event has no faces
        if (!event?.faces || event?.faces.length === 0) {
            // no faces on the event, so only return true if filter contains none.
            return filterState.ids.indexOf(_none) > -1;
        }

        // event has faces
        return Object.keys(event.faces).some(key => {
            // the filter has this face or (the face is not recognized and the filter contains unknown)
            return filterState.ids.indexOf(key) > -1;
        });
    }

    function smartFilter(event, filterState) {
        const smart = filterState.smart;
        if (!smart) {
            return true;
        }
        const tags = event.tags ?? {};
        return ((smart.includes(_person) && tags.peopleCount > 0)
                || (smart.includes(_object) && tags.objectCountAdjusted > 0)
                || (smart.includes(_car) && tags.carCount > 0)
                || (smart.includes(_pet) && ((tags.dogCount > 0) || (tags.catCount > 0)))
                || (smart.includes(_none) && !tags.peopleCount
                && !tags.objectCountAdjusted && !tags.carCount
                && !((tags.dogCount > 0) || (tags.catCount > 0))));
    }

    // properties filter. For example: delivery
    // eslint-disable-next-line complexity
    function propsFilter(event, filterState) {
        const props = filterState?.props;
        if (!Array.isArray(props)) {
            return true;
        }
        const tags = event.tags ?? {};
        return ((props.includes(_delivery) && tags.delivery)
            || (props.includes(_garageOpen) && tags.garageDoorOpen)
            || (props.includes(_garageClose) && tags.garageDoorClose)
            || (props.includes(_pin) && (tags.pinUsed || tags.invalidPin))
            || (props.includes(_call) && tags.call)
            || (props.includes(_autoSecure) && tags.autoSecureEventId))
            || (props.includes(_animal) && tags.animalCount)
            || (props.includes(_vehicle) && tags.movingVehicleCount)
            || (props.includes(_package) && tags.movingPackageCount);
    }

    // other filter. For example: favorite
    function otherFilter(event, filterState) {
        if (!Array.isArray(filterState.other)) {
            return true;
        }
        return (event.favorite && filterState.other.includes(_favorite))
            || (event.type === _highlightType && filterState.other.includes(_highlight));
    }

    /**
     * @description if filterContext.owerAlias, then can only have devices for that user
     * @param {FilterContext} filterContext
     * @param {FilterState} filter
     */
    function onlyDevicesForOwner(filterContext, filter, traceId) {
        if (filterContext?.ownerAlias && filter?.devices) {
            let i = 0;
            while (i < filter.devices.length && filter.devices.length > 0) {
                deviceService.get({
                    id: filter.devices[i],
                    traceId: traceId
                }).pipe(take(1)).subscribe(device => {
                    if (device && (util.aliasWithNoGuidPrefix(device.ownership.ownerAlias)
                            !== util.aliasWithNoGuidPrefix(filterContext.ownerAlias))) {
                        filter.devices.splice(i, 1);
                    } else {
                        i += 1;
                    }
                });
            }
            if (filter.devices.length === 0) {
                delete filter.devices;
            }
        }
    }

    function createFilter(filterContext, traceId) {
        if (typeof filterContext === _object) {
            Object.keys(filterContext).forEach(function(key) {
                if (key !== _deviceId && key !== _ownerAlias) {
                    delete filterContext[key];
                }
            });
            if (!filterContext.ownerAlias) {
                delete filterContext.ownerAlias;
            } else {
                filterContext.ownerAlias = util.aliasWithNoGuidPrefix(filterContext.ownerAlias);
            }
        }

        // create with all possible properties set to improve performance
        const result = {
            spec: filterContext ?? {},
            t: Math.round(_window.Date.now() / 1000),
            devices: undefined, // active devices
            smart: undefined, // smart filters
            props: undefined, // delivery and others in the future
            other: undefined, // favorite
            ids: undefined // identities of people seen
        };

        // init filterState accroding to spec
        if (filterContext && filterContext.deviceId) {
            result.devices = [ filterContext.deviceId ];
        }
        onlyDevicesForOwner(filterContext, result, traceId);

        return result;
    }

    function setStateForArray(filter, modifiedFilter, key) {
        if (Array.isArray(modifiedFilter[key]) && modifiedFilter[key].length > 0) {
            // create array if not there
            filter[key] ??= [];
            // ensure array is cleared out
            filter[key].splice(0);
            // copy in new data
            util.merge(filter[key], modifiedFilter[key]);
        } else {
            // remove the filter array since there are no items in the modified array
            filter[key] = undefined;
        }
    }

    /**
     * @description Saves filters in uiSettings
     * @returns {Promise} Resolves or rejects to the filters collection
     */
    function saveFilters(filters, traceId) {
        // f3 === filters version 3
        const f3 = util.merge([], filters);

        // Remove filters with no settings so they are not saved
        let i = 0;
        let filter;
        while (i < f3.length) {
            filter = f3[i];
            if (!filter.devices && !filter.ids && !filter.smart && !filter.props && !filter.other) {
                f3.splice(i, 1);
            } else {
                i += 1;
            }
        }

        userService.setUiSettings({
            delta: {
                [partnerAppKey()]: {
                    f3: f3
                }
            },
            traceId: traceId
        });
    }

    function uiSettingsToFilters(uiSettings) {
        const key = partnerAppKey();
        const filters = [];
        if (uiSettings[key] && uiSettings[key].f3) {
            const f3 = uiSettings[key].f3;
            const now = Math.round(_window.Date.now() / 1000);
            f3.forEach(filter => {
                if (!filterLifeSeconds() || (filter.t + filterLifeSeconds()) > now) {
                    // only use filters that are not too old
                    filters.push(filter);
                }
            });
        }
        return filters;
    }

    // ****************************
    // public members
    // ****************************

    /**
     * @description Called by clients to test a single event against the current filterState
     * @param {Object} event An event Object
     */
    _eventFilter.isActive = function(event) {
        const filterInfo = filterInfoSubject.getValue();
        if (filterInfo) {
            const filterState = filterInfo.filterState;
            return filterState
                && devicesFilter(event, filterState)
                && smartFilter(event, filterState)
                && identityFilter(event, filterState)
                && propsFilter(event, filterState)
                && otherFilter(event, filterState)
                && internalFilter(event, filterState);
        }
    };

    /**
     * @returns {Observable<FilterInfo>} emits any time the internal filter state changes as a result of
     *     updating filterContext or changes to the filter settings.
     */
    _eventFilter.get = function() {
        return filterInfoSubject.asObservable();
    };

    /**
     * @description This will update and save the filters, creating a new filter if necessary.
     * @param {Object} filterContext specification object for the type of filter to get
     * @param {String} filterContext.deviceId set to deviceId if the filter is for viewing a single device
     * @param {String} filterContext.ownerAlias only filter for cameras owned by this user,
     *     or undefined for all users' devices including shared
     * @param {Object} modifiedFilter Update the current filterState with this value.
     * @param {Object} params
     * @param {String} [params.traceId]
     */
    _eventFilter.set = function(filterContext, modifiedFilter, params) {

        // ensure filters are loaded before continuing
        if (!filters) {
            if (!uiSettingsSubscription) {
                uiSettingsSubscription = userService.getUiSettings(params).subscribe({
                    next: uiSettings => {
                        if (uiSettings) {
                            filters = uiSettingsToFilters(uiSettings);
                            _eventFilter.set(filterContext, modifiedFilter, params);
                        }
                    }
                });
            }
            return;
        }

        let filter = filters.find(byFilterContext, filterContext ?? {});
        if (!filter) {
            filter = createFilter(filterContext, params?.traceId);
            filters.push(filter);
        }

        if (modifiedFilter) {
            // if filterContext.deviceId was specified, then devices cannot change
            if (!filter.spec.deviceId) {
                setStateForArray(filter, modifiedFilter, _devices);
            }
            setStateForArray(filter, modifiedFilter, _ids);
            setStateForArray(filter, modifiedFilter, _smart);
            setStateForArray(filter, modifiedFilter, _props);
            setStateForArray(filter, modifiedFilter, _other);
            onlyDevicesForOwner(filter.spec, filter, params?.traceId);
            if (filter) {
                filter.t = Math.round(_window.Date.now() / 1000);
            }
            saveFilters(filters, params?.traceId);
        }

        filterInfoSubject.next({
            filterContext: util.merge({}, filter.spec),
            filterState: util.merge({}, filter)
        });
    };

    /**
     * @returns {FilterInfo} Returns the current filter info in a synchronous way
     */
    _eventFilter.getState = function() {
        return filterInfoSubject.getValue();
    };

    /**
     * @returns {Boolean} true if there is currently a filter applied
     */
    _eventFilter.isFilterApplied = function() {
        const filterInfo = filterInfoSubject.getValue();
        if (filterInfo) {
            const filterState = filterInfo.filterState;
            if (filterState && filterState.spec) {
                if (filterState[_devices] && !filterState.spec.deviceId) {
                    return true;
                }
                if (filterState[_ids] || filterState[_smart]
                        || filterState[_props] || filterState[_other]) {
                    return true;
                }
            }
        }
        return false;
    };

    /**
     * @description Clears all internal filter data as if filters were never loaded.
     */
    _eventFilter.clear = function() {
        filterInfoSubject.next();
        if (uiSettingsSubscription) {
            uiSettingsSubscription.unsubscribe();
        }
        filters = undefined;
    };

    /**
     * @description Allows clients to test if an event will always be filtered out.
     * @type {Function}
     */
    _eventFilter.internalFilter = internalFilter;

    return Object.freeze(_eventFilter);
}

export default createEventFilter;
